import { ref, computed, type Ref } from "vue";
import { getSessionUserInput } from "../api";
import { isNumericFieldValueValid, VuetifyValidation } from "@/helpers/Validation";
import { isUserInputFieldVisible } from "../utils/fieldVisibility";
import { createLatestRequestScope } from "@/composables/useLatestRequest";
import type { RunAgentTask, UserInputTaskConfig, KnowledgeRequirement } from "../types";

export interface UseRunAgentUserInputOptions {
	loadedPackTasks: Ref<RunAgentTask[]>;
}

export function useRunAgentUserInput(options: UseRunAgentUserInputOptions) {
	const { loadedPackTasks } = options;

	const userInputRequirements = ref<KnowledgeRequirement[]>([]);
	const userInputTaskConfigs = ref<UserInputTaskConfig[]>([]);
	const userInputData = ref<Record<string, Record<string, any>>>({});
	const userInputValid = ref<Record<string, boolean>>({});
	const loadingUserInputRequirements = ref(false);

	// LSP-16996: orders overlapping fetchUserInputConfigsForPack() invocations. Only the latest
	// invocation may apply/clean state; an earlier one that a newer invocation superseded no-ops.
	const userInputRequests = createLatestRequestScope();

	const hasUserInputItems = computed(() => {
		return userInputRequirements.value.length > 0 || userInputTaskConfigs.value.length > 0;
	});

	const extractRawValue = (val: any): any => (val && typeof val === "object" && "value" in val && "fieldLabel" in val ? val.value : val);

	const isFieldFilled = (val: any): boolean => {
		const rawVal = extractRawValue(val);
		if (rawVal === null || rawVal === undefined || rawVal === "") return false;
		if (Array.isArray(rawVal) && rawVal.length === 0) return false;
		return true;
	};

	// LSP-21386: whitespace-only text and an unticked checkbox do not ANSWER a required field — both
	// used to count as filled, so a Spark could run on inputs the user never actually supplied.
	// UserInputFormRenderer's required rule applies the same test so the red field and the gate agree.
	// Constraint checks keep using isFieldFilled: "   " in an email field is still a malformed value.
	const isRequiredFieldAnswered = (val: any, fieldType?: string): boolean => {
		if (!isFieldFilled(val)) return false;
		const rawVal = extractRawValue(val);
		if (typeof rawVal === "string" && rawVal.trim() === "") return false;
		if (fieldType === "checkbox" && rawVal === false) return false;
		return true;
	};

	const isUrlValid = (value: unknown): boolean => {
		try {
			new URL(String(value));
			return true;
		} catch {
			return false;
		}
	};

	// Mirrors UserInputFormRenderer.getValidationRules so the submit gate agrees with the
	// field's red error state (LSP-16465): a filled value that violates its
	// length/range/pattern constraints blocks submit. Typing past a maxLength is still
	// allowed in the input (warn, never hard-cap) — but the user must trim it to continue.
	const emailRule = VuetifyValidation.EmailValidation("Invalid email format").isValid;
	const fieldPassesConstraints = (field: any, val: any): boolean => {
		const validation = field?.validation;
		if (!isFieldFilled(val)) return true; // required-emptiness handled separately
		const raw = extractRawValue(val);
		const str = String(raw ?? "");
		if (field?.type === "email" && emailRule(raw) !== true) return false;
		if (field?.type === "url" && !isUrlValid(raw)) return false;
		if (!validation) return true;
		if (validation.minLength !== undefined && str.length < validation.minLength) return false;
		if (validation.maxLength !== undefined && str.length > validation.maxLength) return false;
		if (validation.min !== undefined && Number(raw) < validation.min) return false;
		if (validation.max !== undefined && Number(raw) > validation.max) return false;
		if (validation.pattern) {
			try {
				if (!new RegExp(validation.pattern).test(str)) return false;
			} catch {
				// invalid pattern config — don't block submit on a broken regex
			}
		}
		return true;
	};

	/**
	 * The fields the submit gate is refusing on, by what the user has to do about them (LSP-19461):
	 * `missing` is a visible required field with nothing in it, `invalid` is a filled field whose value
	 * breaks its own constraints. ONE walk feeds both this and `allUserInputValid`, so the footer can
	 * never name a field the gate is not actually holding the run on, or hold the run on one it does
	 * not name.
	 */
	const userInputProblems = computed<{ missing: string[]; invalid: string[] }>(() => {
		const missing: string[] = [];
		const invalid: string[] = [];
		for (const config of userInputTaskConfigs.value) {
			const key = `task_${config.taskId}`;
			const data = userInputData.value[key] || {};

			// Resolve raw form values (strip the enriched {value, fieldLabel} wrapper) so the
			// visibility predicate sees plain primitives — matching what the renderer stores in
			// its local formValues ref. (guards LSP-16496)
			const rawFormValues: Record<string, any> = {};
			for (const field of config.inlineFormConfig.fields as any[]) {
				rawFormValues[field.key] = extractRawValue(data[field.key]);
			}

			for (const field of config.inlineFormConfig.fields as any[]) {
				// Skip fields that are not currently rendered — the user cannot see or fix them, so
				// they must not gate submit. Same predicate as UserInputFormRenderer.visibleFields,
				// via the shared util. (guards LSP-16496 / LSP-16465 BugBot)
				if (!isUserInputFieldVisible(field, rawFormValues)) continue;

				const rawVal = extractRawValue(data[field.key]);
				const label: string = field.label || field.key;

				if (field.validation?.required && !isRequiredFieldAnswered(data[field.key], field.type)) {
					missing.push(label);
					continue;
				}

				// Number fields render as type="text" (LSP-16496) so non-numeric text such as "e"
				// can reach the model. Block the gate on invalid numeric content.
				if (field.type === "number" && !isNumericFieldValueValid(rawVal)) {
					invalid.push(label);
					continue;
				}

				// Length/range/pattern constraints (LSP-16465 warn-don't-cap submit gate): a filled
				// value over maxLength / out of range / failing pattern blocks submit. The counter
				// still warns and typing is never hard-capped.
				if (!fieldPassesConstraints(field, data[field.key])) invalid.push(label);
			}
		}

		return { missing, invalid };
	});

	const missingRequiredFieldLabels = computed(() => userInputProblems.value.missing);
	const invalidFieldLabels = computed(() => userInputProblems.value.invalid);
	const allUserInputValid = computed(() => userInputProblems.value.missing.length === 0 && userInputProblems.value.invalid.length === 0);

	const clearUserInputState = () => {
		userInputRequirements.value = [];
		userInputTaskConfigs.value = [];
		userInputData.value = {};
		userInputValid.value = {};
	};

	const fetchUserInputConfigsForPack = async (
		packId: string,
		externalId?: string | null,
		externalSource?: string | null,
		externalType?: string | null,
		// LSP-16996: currency check tied to the CALLER's load (loadPackDetails' latest-request guard).
		// Combined with the sub-call scope below it enforces two rules on the awaited result:
		//   - a NEWER fetch invocation (sub-superseded) owns the state → this one no-ops (never wipes it);
		//   - the LATEST fetch invocation whose load has since gone stale → cleans up its own pre-await
		//     config/data mutations so a bailed/superseded selection can't leave stale user-input behind.
		// Defaults to always-current for standalone callers.
		isStale: () => boolean = () => false
	) => {
		const { requestId } = userInputRequests.startRequest();
		loadingUserInputRequirements.value = true;
		try {
			// Initialize user input data
			userInputData.value = {};
			userInputValid.value = {};

			// 1. Knowledge-based user-input requirements are no longer used
			// (user-input is now a task type, not a knowledge type)
			userInputRequirements.value = [];

			// 2. Extract inline form configs from user-input tasks
			const mediaOrders = loadedPackTasks.value
				.filter((task) => task.type === "media" || task.type === "real-time-media")
				.map((task) => task.order)
				.filter((order): order is number => order !== undefined);
			const firstMediaOrder = mediaOrders.length ? Math.min(...mediaOrders) : undefined;
			const userInputTasks = loadedPackTasks.value.filter(
				(task) =>
					task.type === "user-input" &&
					task.inlineFormConfig?.fields?.length &&
					(firstMediaOrder === undefined || task.order === undefined || task.order <= firstMediaOrder)
			);
			userInputTaskConfigs.value = userInputTasks.map((t) => ({
				taskId: t.id,
				taskName: t.name,
				description: t.description || null,
				inlineFormConfig: t.inlineFormConfig!
			}));

			for (const config of userInputTaskConfigs.value) {
				const key = `task_${config.taskId}`;
				userInputData.value[key] = {};
				const hasRequiredFields = config.inlineFormConfig.fields.some((f: any) => f.validation?.required);
				userInputValid.value[key] = !hasRequiredFields;
			}

			// Load previously saved user input data
			if (externalId && externalSource && externalType) {
				let savedResponse: any;
				try {
					savedResponse = await getSessionUserInput(externalSource, externalType, externalId, packId);
				} catch (e) {
					// A newer fetch owns the state now — leave it. If we are the latest fetch but our load
					// went stale, clean up our own pre-await config setup.
					if (userInputRequests.isSuperseded(requestId)) return;
					if (isStale()) {
						clearUserInputState();
						return;
					}
					console.error("Failed to load saved user input data:", e);
					return;
				}
				// A newer fetch invocation started while awaiting — it owns the state; do not touch it.
				if (userInputRequests.isSuperseded(requestId)) return;
				// Latest fetch, but the load that triggered it was superseded — remove the pre-await state
				// it left so a bailed/superseded selection doesn't surface stale user input.
				if (isStale()) {
					clearUserInputState();
					return;
				}
				const savedData = (savedResponse as any)?.userInput?.userInputData ?? savedResponse;
				if (savedData) {
					for (const key of Object.keys(userInputData.value)) {
						if (savedData[key]) {
							for (const fieldKey of Object.keys(savedData[key])) {
								userInputData.value[key][fieldKey] = savedData[key][fieldKey];
							}
						}
					}
				}
			}
		} catch (e) {
			// A newer fetch owns the state; don't clobber it.
			if (userInputRequests.isSuperseded(requestId)) {
				return;
			}
			console.error("Failed to fetch user input configs:", e);
			userInputRequirements.value = [];
			userInputTaskConfigs.value = [];
		} finally {
			// Only the latest fetch invocation owns the loading flag.
			if (!userInputRequests.isSuperseded(requestId)) {
				loadingUserInputRequirements.value = false;
			}
		}
	};

	const handleUserInputUpdate = (key: string, data: Record<string, any>) => {
		userInputData.value[key] = data;
	};

	const handleUserInputValidation = (key: string, valid: boolean) => {
		userInputValid.value[key] = valid;
	};

	const reset = () => {
		userInputRequirements.value = [];
		userInputTaskConfigs.value = [];
		userInputData.value = {};
		userInputValid.value = {};
		// LSP-16996: also clear the loading flag. A reset (e.g. from loadPackDetails' reset-at-top when
		// a load bails on empty media) must not leave a stale spinner up while an older in-flight
		// user-input fetch is still pending.
		loadingUserInputRequirements.value = false;
		// Supersede any in-flight fetch so its late resolution cannot re-touch state after this reset.
		userInputRequests.startRequest();
	};

	return {
		userInputRequirements,
		userInputTaskConfigs,
		userInputData,
		userInputValid,
		loadingUserInputRequirements,
		hasUserInputItems,
		allUserInputValid,
		missingRequiredFieldLabels,
		invalidFieldLabels,
		fetchUserInputConfigsForPack,
		handleUserInputUpdate,
		handleUserInputValidation,
		reset
	};
}
